import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import useFetch from '../../../../hooks/fetch.hook';
import SchoolApiModel from '../../models/school.api-model';
import type ISchool from '../../../../models/pa-egna-ben/school.model';
import TextFieldFormGroup from '../../../Shared/TextFieldFormGroup';
import SelectFormGroup from '../../../Shared/SelectFormGroup';
import InformationBox from '../../Shared/InformationBox';
import { SelectSchoolField, SelectSchoolMode } from './select-school.enum';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';

export interface ISelectSchoolValue {
  mode: SelectSchoolMode;
  school: ISchool | undefined;
}

export interface ISelectSchoolState {
  value: ISelectSchoolValue;
  validationInfo: ValidationInfo;
}

export interface ISelectSchoolProps {
  municipalityGid: string | undefined;
  inputId: string;
  showNewSchoolRadio?: boolean;
  showPostalInfo?: boolean;
  state: ISelectSchoolState;
  onChange?: (value: ISelectSchoolValue | undefined) => void;
  onKeyUp?: (field: SelectSchoolField) => void;
}

export default forwardRef<IValidatable, ISelectSchoolProps>(function SelectSchool(
  { municipalityGid, inputId, showNewSchoolRadio = true, showPostalInfo = true, state, onChange = undefined, onKeyUp = undefined }: ISelectSchoolProps,
  ref
) {
  // === REFS ===
  const existingSchoolRef = useRef<IValidatable>(null);
  const nameRef = useRef<IValidatable>(null);
  const streetAddressRef = useRef<IValidatable>(null);
  const postalCodeRef = useRef<IValidatable>(null);
  const cityRef = useRef<IValidatable>(null);

  // === STATE ===
  const [getSchools] = useFetch<SchoolApiModel[]>(`/api/paegnaben/teacher/schools?municipalityGid=${municipalityGid}`, !municipalityGid);

  // === VARIABLES ===
  const labelText = 'Skola';
  const errorTextId = `${inputId}-error`;
  const schools = getSchools?.data ? getSchools.data.map(SchoolApiModel.mapToBusinessModel) : undefined;
  const isDisabled = !municipalityGid;

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      if (state.value.mode === SelectSchoolMode.chooseinlist) {
        return new ValidationInfo(`${labelText} - `, [], {
          existingSchool: existingSchoolRef.current?.checkValidity() ?? new ValidationInfo(),
        });
      }
      if (state.value.mode === SelectSchoolMode.createnew) {
        return new ValidationInfo(`${labelText} - `, [], {
          schoolName: nameRef.current?.checkValidity() ?? new ValidationInfo(),
          schoolStreetAddress: streetAddressRef.current?.checkValidity() ?? new ValidationInfo(),
          schoolPostalCode: postalCodeRef.current?.checkValidity() ?? new ValidationInfo(),
          schoolCity: cityRef.current?.checkValidity() ?? new ValidationInfo(),
        });
      }
      return new ValidationInfo();
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(`${labelText} - `, [], {
        existingSchool: existingSchoolRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo(),
        schoolName: nameRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo(),
        schoolStreetAddress: streetAddressRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo(),
        schoolPostalCode: postalCodeRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo(),
        schoolCity: cityRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo(),
      });
    },
  }));

  // === EVENT HANDLERS ===
  function handleSchoolChange(value: string): void {
    if (!municipalityGid) {
      return;
    }
    onKeyUp?.(SelectSchoolField.existingSchool);
    onChange?.({
      mode: SelectSchoolMode.chooseinlist,
      school: schools?.find((x) => x.id === parseInt(value, 10)) ?? { id: -1, name: '', streetAddress: '', postalCode: '', city: '', municipalityGid },
    });
  }
  // === RENDER ===
  if (getSchools.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getSchools.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <div className="mb-3">
      <label className="form-label" htmlFor="select-school">
        {labelText}
      </label>
      {showNewSchoolRadio && (
        <div className="mb-2">
          <div className="form-check form-check-inline">
            <input
              className={`form-check-input`}
              type="radio"
              name="radioChooseOrCreateSchool"
              id="radioChooseInList"
              disabled={isDisabled}
              checked={state.value.mode === SelectSchoolMode.chooseinlist}
              onChange={() => {
                onKeyUp?.(SelectSchoolField.mode);
                onChange?.({ mode: SelectSchoolMode.chooseinlist, school: { id: -1, name: '', streetAddress: '', postalCode: '', city: '', municipalityGid: municipalityGid ?? '' } });
              }}
            />
            <label className="form-check-label" htmlFor="radioChooseInList">
              Välj i lista
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="radioChooseOrCreateSchool"
              id="radioCooseCreateNew"
              disabled={isDisabled}
              checked={state.value.mode === SelectSchoolMode.createnew}
              onChange={() => {
                onKeyUp?.(SelectSchoolField.mode);
                onChange?.({ mode: SelectSchoolMode.createnew, school: { id: -1, name: '', streetAddress: '', postalCode: '', city: '', municipalityGid: municipalityGid ?? '' } });
              }}
            />
            <label className="form-check-label" htmlFor="radioCooseCreateNew">
              Lägg till ny skola
            </label>
          </div>
        </div>
      )}
      {state.value.mode === SelectSchoolMode.chooseinlist && (
        <SelectFormGroup
          ref={existingSchoolRef}
          id="select-school"
          serverValidationName="schoolId"
          placeholder="Välj skola"
          disabled={isDisabled}
          required={true}
          hideRequiredAsterisk={true}
          options={
            schools
              ?.sort((a, b) => a.name.localeCompare(b.name))
              .map((m) => ({
                text: m.name ?? '',
                value: m.id.toString() ?? '',
              })) ?? [{ text: state.value.school?.name ?? '', value: state.value.school?.id.toString() ?? '' }]
          }
          state={{
            value: state.value.school?.id !== -1 ? state.value.school?.id.toString() : '',
            validationInfo: state.validationInfo.children.existingSchool ?? new ValidationInfo(),
          }}
          onChange={handleSchoolChange}
        />
      )}
      {state.value.mode === SelectSchoolMode.createnew && (
        <TextFieldFormGroup
          ref={nameRef}
          labelText="Skolans namn"
          required={true}
          hideRequiredAsterisk={true}
          inputId="school-name-input"
          type="text"
          serverValidationName="schoolName"
          maxLength={35}
          state={{ value: state.value.school ? state.value.school.name : '', validationInfo: state.validationInfo.children.schoolName ?? new ValidationInfo() }}
          onChange={(value) => {
            if (state.value.school) {
              onChange?.({ ...state.value, school: { ...state.value.school, name: value } });
            }
          }}
          onKeyUp={() => onKeyUp?.(SelectSchoolField.name)}
        />
      )}
      <>
        {showPostalInfo && (
          <>
            <h2 className="h3">Postadress</h2>
            <InformationBox type={InformationBoxType.Default} headingElement="Postadressen behövs för att skicka ut startmaterialet till din klass" className="mb-3" />
          </>
        )}
        <TextFieldFormGroup
          ref={streetAddressRef}
          labelText="Adress"
          required={true}
          hideRequiredAsterisk={true}
          disabled={isDisabled || state.value.mode === SelectSchoolMode.chooseinlist}
          inputId="school-street-address-input"
          type="text"
          className="mb-3"
          serverValidationName="schoolStreetAddress"
          maxLength={35}
          state={{ value: state.value.school ? state.value.school.streetAddress : '', validationInfo: state.validationInfo.children.schoolStreetAddress ?? new ValidationInfo() }}
          onChange={(value) => {
            if (state.value.school) {
              onChange?.({ ...state.value, school: { ...state.value.school, streetAddress: value } });
            }
          }}
          onKeyUp={() => onKeyUp?.(SelectSchoolField.streetAddress)}
        />
        <div className="row">
          <TextFieldFormGroup
            ref={postalCodeRef}
            labelText="Postnummer"
            required={true}
            hideRequiredAsterisk={true}
            disabled={isDisabled || state.value.mode === SelectSchoolMode.chooseinlist}
            inputId="school-postal-code-input"
            type="text"
            maxLength={6}
            className="col-md-6 mb-3 mb-md-0"
            serverValidationName="schoolPostalCode"
            state={{ value: state.value.school ? state.value.school.postalCode : '', validationInfo: state.validationInfo.children.schoolPostalCode ?? new ValidationInfo() }}
            onChange={(value) => {
              if (state.value.school) {
                onChange?.({ ...state.value, school: { ...state.value.school, postalCode: value } });
              }
            }}
            onKeyUp={() => onKeyUp?.(SelectSchoolField.postalCode)}
          />
          <TextFieldFormGroup
            ref={cityRef}
            labelText="Ort"
            required={true}
            hideRequiredAsterisk={true}
            disabled={isDisabled || state.value.mode === SelectSchoolMode.chooseinlist}
            inputId="school-city-input"
            type="text"
            className="col-md-6 mb-0"
            serverValidationName="schoolCity"
            maxLength={27}
            state={{ value: state.value.school ? state.value.school.city : '', validationInfo: state.validationInfo.children.schoolCity ?? new ValidationInfo() }}
            onChange={(value) => {
              if (state.value.school) {
                onChange?.({ ...state.value, school: { ...state.value.school, city: value } });
              }
            }}
            onKeyUp={() => onKeyUp?.(SelectSchoolField.city)}
          />
        </div>
      </>
      {state.validationInfo.isInvalid && (
        <div id={errorTextId} className="invalid-feedback">
          {state.validationInfo.getErrors().map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
    </div>
  );
});
